import { useState } from 'react';
import { formatDraftTime } from '@/lib/draftTime';
import { useNFLLeague } from '@/contexts/NFLLeagueContext';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  CheckCircle2, Circle, Copy, Share2, Users, Calendar, Play,
  ChevronRight, Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { useCommissionerFeeStatus } from '@/hooks/useCommissionerFeeStatus';

interface NFLCommissionerChecklistProps {
  onNavigate: (tab: string) => void;
}

interface Step {
  id: string;
  label: string;
  description: string;
  isComplete: boolean;
  action?: () => void;
  actionLabel?: string;
}

export default function NFLCommissionerChecklist({ onNavigate }: NFLCommissionerChecklistProps) {
  const { currentLeague, teams } = useNFLLeague();
  const { user } = useAuth();
  const { toast } = useToast();
  // SAB-591: paid leagues add a "pay your entry fee" step (server refuses
  // draft start until the commissioner's fee is in).
  const feeStatus = useCommissionerFeeStatus(currentLeague?.id);
  const [copiedCode, setCopiedCode] = useState(false);

  if (!currentLeague || currentLeague.commissioner_id !== user?.id) return null;

  const inviteCode = (currentLeague as any)?.invite_code || '';
  /**
   * SAB-1000 (sibling audit): read the declared field, do not invent a capacity.
   *
   * Was `(currentLeague as any)?.max_teams || (currentLeague as any)?.team_count || 12`.
   * Three problems, in increasing order of severity:
   *
   *   - the `as any` casts are unnecessary; max_teams is declared on NFLLeague.
   *   - team_count is a DIFFERENT quantity (teams present, not capacity), so the first
   *     fallback silently answers a different question -- "3/3 teams joined, complete" for
   *     a league still waiting on nine more.
   *   - `|| 12` fabricates a capacity, and `||` also swallows a legitimate 0.
   *
   * Measured before changing it: max_teams is NEVER null and NEVER 0 on either lineage
   * (prod range 8..14, 10 of 22 leagues are not 12), so this fallback is dead code today,
   * not a live misstatement like the $1,500 banner. It is fixed as the latent hazard it is
   * -- the day max_teams goes null, a 14-team league would quietly report "of 12".
   */
  const maxTeams = currentLeague?.max_teams ?? null;
  const filledTeams = teams.length;
  const draftStart = (currentLeague as any)?.draft_start_time;
  const draftStatus = (currentLeague as any)?.draft_status || 'pending';

  const copyInviteCode = () => {
    if (!inviteCode) return;
    navigator.clipboard.writeText(inviteCode);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
    toast({ title: 'Copied!', description: 'Invite code copied to clipboard' });
  };

  const shareInvite = async () => {
    // SAB-593: derive from the current origin so a preview deploy's share link
    // points at the preview (where the league exists), not hardcoded prod.
    const inviteLink = `${window.location.origin}/nfl/join/${inviteCode}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Join ${currentLeague.name} on Sabotage Fantasy`,
          text: `Join my NFL league! Use code ${inviteCode}`,
          url: inviteLink,
        });
      } catch { /* cancelled */ }
    } else {
      navigator.clipboard.writeText(inviteLink);
      toast({ title: 'Copied!', description: 'Invite link copied' });
    }
  };

  // SAB-591: the pay action itself lives in the lobby banner rendered directly
  // below this checklist — scroll it into view rather than duplicating the
  // money flow here.
  const scrollToFeeBanner = () => {
    document.getElementById('commissioner-fee-banner')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const steps: Step[] = [
    {
      id: 'create',
      label: 'Create League',
      description: 'Your league is live!',
      isComplete: true,
    },
    // Paid leagues only; hidden until the paid check resolves so a paid
    // commissioner never sees it flash unchecked.
    ...(feeStatus.required && !feeStatus.loading ? [{
      id: 'pay-fee',
      label: `Pay your entry fee ($${feeStatus.entryFee})`,
      description: feeStatus.paid
        ? "You're locked in — fee paid"
        : 'The draft is locked until your buy-in is in',
      isComplete: feeStatus.paid,
      action: scrollToFeeBanner,
      actionLabel: 'Pay',
    }] : []),
    {
      id: 'share',
      label: 'Share Invite Link',
      description: inviteCode
        ? `Code: ${inviteCode}. Share it with friends`
        : 'Generate an invite code',
      isComplete: !!inviteCode,
      action: shareInvite,
      actionLabel: 'Share',
    },
    {
      id: 'fill',
      label: 'Fill League',
      // Capacity unknown -> report what IS known rather than inventing a denominator.
      description: maxTeams === null ? `${filledTeams} teams joined` : `${filledTeams}/${maxTeams} teams joined`,
      isComplete: maxTeams !== null && filledTeams >= maxTeams,
    },
    {
      id: 'draft-date',
      label: 'Set Draft Date',
      description: draftStart
        ? `Scheduled: ${formatDraftTime(draftStart)}`   /* SAB-788: was date-only, and without a zone */
        : 'Pick a date and time for your draft',
      isComplete: !!draftStart,
      action: () => onNavigate('settings'),
      actionLabel: 'Settings',
    },
    {
      id: 'start-draft',
      label: 'Start Draft',
      description: draftStatus === 'in_progress'
        ? 'Draft is live!'
        : draftStatus === 'completed'
        ? 'Draft complete!'
        : 'Kick off when everyone is ready',
      isComplete: draftStatus === 'in_progress' || draftStatus === 'completed',
      action: () => onNavigate('draft'),
      actionLabel: 'Go to Draft',
    },
  ];

  const completedCount = steps.filter(s => s.isComplete).length;
  const allComplete = completedCount === steps.length;
  const progress = (completedCount / steps.length) * 100;

  if (allComplete) return null;

  return (
    <Card className="bg-card border-border overflow-hidden">
      <CardContent className="p-4 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Setup Checklist</p>
            <p className="text-[10px] text-muted-foreground mt-0.5">
              {completedCount}/{steps.length} complete
            </p>
          </div>
          <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-[10px]">
            Commissioner
          </Badge>
        </div>

        {/* Progress bar */}
        <div className="h-1.5 bg-muted/50 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full bg-primary transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Steps */}
        <div className="space-y-1">
          {steps.map((step, i) => {
            const isNext = !step.isComplete && (i === 0 || steps[i - 1].isComplete);
            return (
              <div
                key={step.id}
                className={cn(
                  'flex items-start gap-3 p-2.5 rounded-lg transition-colors',
                  isNext && 'bg-primary/5 border border-primary/20',
                  step.isComplete && 'opacity-60',
                )}
              >
                {/* Icon */}
                <div className="mt-0.5 shrink-0">
                  {step.isComplete ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                  ) : (
                    <Circle className={cn('h-4 w-4', isNext ? 'text-primary' : 'text-muted-foreground/40')} />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <p className={cn(
                    'text-sm font-medium',
                    step.isComplete ? 'text-muted-foreground line-through' : 'text-foreground',
                  )}>
                    {step.label}
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">{step.description}</p>
                </div>

                {/* Action */}
                {step.action && !step.isComplete && isNext && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={step.action}
                    className="h-7 px-2 text-[10px] text-primary shrink-0"
                  >
                    {step.actionLabel}
                    <ChevronRight className="h-3 w-3 ml-0.5" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        {/* Quick copy code */}
        {inviteCode && (
          <div className="flex items-center gap-2 p-2.5 rounded-lg bg-muted/30 border border-border">
            <code className="flex-1 text-sm font-mono font-bold text-primary tracking-[0.2em]">
              {inviteCode}
            </code>
            <Button variant="ghost" size="sm" onClick={copyInviteCode} className="h-7 px-2">
              {copiedCode ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
            </Button>
            <Button variant="ghost" size="sm" onClick={shareInvite} className="h-7 px-2">
              <Share2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
